<template>
	<v-card class="action-resources-page pa-7 pb-1 mt-8 pb-8" style="border-radius: 10px; max-width: 600px;" flat>
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<v-card-text v-else-if="isBlocked !== null" class='mt-5'>
			<ButtonBack />

			<v-skeleton-loader
				style="max-width: 600px;"
				v-bind="attrs"
				type="card"
				v-if="isLoading"
			>
			</v-skeleton-loader>

			<h3 
				v-if="!isLoading && pageTitle" 
				class="action-resources-page__title"
			>
				{{ pageTitle }}
			</h3>

			<UploadList
				v-if='!isLoading && uploads.length > 0'
				:resourcesItems="uploads"
			/>
			<LinkList
				v-if='!isLoading && links.length > 0'
				:resourcesItems="links"
			/>

			<div
				v-if="!isLoading && isCurrentPageListEmpty"
				class="mt-5 align-center"
			>
				<NewsFeedEmpty
					title="Action resources is empty at the moment."
					description="Check back later."
				/>
			</div>
			<div
				class="d-flex my-9 ma-auto justify-center home-page__pagination"
				v-if="!isLoading && (uploads.length > 0 || links.length > 0)"
			>
				<Pagination 
					:page="filters.page"
					:pageCount="pageCount"
					v-on:pageChange="onPageChange"
				/>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import searchIcon from '@/assets/images/search.svg'
import UploadList from '@/components/Resource/UploadList.vue'
import LinkList from '@/components/Resource/LinkList.vue'
import NewsFeedEmpty from '@/components/Post/NewsFeedEmpty.vue'
import Pagination from '@/components/Pagination.vue'
import ButtonBack from '@/components/ButtonBack.vue'
import PageBlocked from '@/components/PageBlocked'
import Block from '@/mixins/Block.js'
import Dwh from '@/mixins/Dwh'

export default {
	name: 'ActionResourcePage',
	mixins: [Block, Dwh],
	components: {
		NewsFeedEmpty,
		LinkList,
		UploadList,
		Pagination,
		ButtonBack,
		PageBlocked
	},
	computed: {
		...mapGetters('resources', ['resourcesList', 'listMeta']),
		...mapGetters('event', [
			'actionFiles',
			'actionLinks',
			'actionFilesMeta',
			'actionLinksMeta',
			'isLoading',
			'event'
		]),

		uploads() {
			if (!this.actionFiles && this.actionFiles.length > 0) {
				return [];
			}

			return this.actionFiles.map(({upload}) => upload) || [];
		},
		links() {
			if (!this.actionLinks && this.actionLinks.length > 0) {
				return [];
			}

			return this.actionLinks || [];
		},
		actionType() {
			return this.links.length > 0 ? 'links' : 'files';
		},
		isCurrentPageListEmpty() {
			const isLinksEmpty = this.links.length <= 0;
			const isFilesEmpty = this.uploads.length <= 0;

			const type = this.links.length > 0 ? 'links' : 'files';

			return isLinksEmpty && type === 'links' ||
				isFilesEmpty && type === 'files';
		},
		pageCount() {
			if (this.actionLinksMeta && this.links.length > 0) {
				return +this.actionLinksMeta.pageCount;
			}

			if (this.actionFilesMeta && this.uploads.length > 0) {
				return +this.actionFilesMeta.pageCount;
			}

			return 0;
		},
		pageTitle() {
			return this.$route.query.title || null
		}
	},
	methods: {
		...mapActions('event', ['getActionLinks', 'getActionFiles']),

		onPageChange(page) {
			if (this.filters.page !== page) {
				const params = {
					search: this.filters.search,
					page
				}
				this.filters.page = page
				const queryString = this.formatFilters(params)
				this.getResources(queryString)
			}
		},
		getResources(queryString) {
			if (this.actionType === 'links') {
				this.getActionLinks( {id: this.$route.params.actionId, queryString} )
			}

			if (this.actionType === 'files') {
				this.getActionFiles( {id: this.$route.params.actionId, queryString} )
			}
		},
		filterSearch(e) {
			clearTimeout(this.timer)

			const params = {
				search: e.target.value,
				page: 1,
			}
			this.filters.page = 1
			const queryString = this.formatFilters(params)

			this.timer = setTimeout(() => {
				this.getResources(queryString)
			}, 500)
		},
		formatFilters(filters) {
			const { search, page } = filters

			let queryString = `page=${page}`

			if (search !== undefined && search.length > 0) {
				queryString += `&search=${search}`
			}

			return queryString
		},
	},
	watch: {
		uploads(newValue) {
			if (newValue.length) {
				const triggerPayload = {
					category: 'Content',
					action: 'view_action_list_of_files_event',
					module: 'Events',
					content_chapter_id: this.event && this.event.chapter_id ? [this.event.chapter_id] : null
				}
				if (this.$route.query.title) {
					triggerPayload.title = this.$route.query.title
				}
				this.sendTrigger(triggerPayload)
			}
		},
		links(newValue) {
			if (newValue.length) {
				const triggerPayload = {
					category: 'Content',
					action: 'view_action_list_of_links_event',
					module: 'Events',
					content_chapter_id: this.event && this.event.chapter_id ? [this.event.chapter_id] : null
				}
				if (this.$route.query.title) {
					triggerPayload.title = this.$route.query.title
				}
				this.sendTrigger(triggerPayload)
			}
		}
	},
	data() {
		return {
			searchIcon,
			successMessage: false,
			errorMessage: false,
			attrs: {
				class: 'mb-6 mt-6',
				elevation: 0,
			},
			filters: {
				page: 1,
				search: '',
			},
			pageBlockedName: 'events'
		}
	},
	created() {
		if (
			this.$route.params.eventId !== undefined &&
			this.$route.params.actionId !== undefined &&
			(!this.isBlocked || this.isBlocked === null)
		) {
			this.getActionFiles({id: this.$route.params.actionId})
			this.getActionLinks({id: this.$route.params.actionId})
		}
	},
}
</script>

<style lang="scss">
.action-resources-page {

	&__title {
		margin-top: 30px;
		font-size: 24px;
		font-weight: 700;
		font-family: Montserrat, sans-serif;
		color: #4A4A4A;
		line-height: 29px;
	}
}
</style>
